import React from 'react';
import {
   Box,
   Typography,
   FormControl,
   Card,
   Checkbox,
   Button,
   CircularProgress,
} from '@mui/material';
import { Container, TextInput } from './style';
import { FaSearch, FaTimes } from 'react-icons/fa';
import backImg from '../../../assets/back.svg';
import { useDispatch, useSelector } from 'react-redux';
import { contactSelector } from '../../utils/selectors/selectors';
import { fetchContacts } from '../../app/features/contacts/contactSlice';
import { addSelectedContacts } from '../../app/features/message/messageSlice';
import InfiniteScroll from 'react-infinite-scroll-component';
import { userLSAuth } from '../../app/features/env';
import noTransIcon from '../../../assets/noTrans.svg';

const ContactSelect = () => {
   const selector = useSelector(contactSelector);
   const dispatch = useDispatch();
   const [search, setSearch] = React.useState('');
   const [sorted, setSorted] = React.useState([]);
   const [checkedList, setCheckedList] = React.useState([]);
   const [itemsChecked, setItemsChecked] = React.useState(false);
   const [page, setPage] = React.useState(1);
   const [loading, setLoading] = React.useState(false);
   const [selectAllLoading, setSelectAllLoading] = React.useState(false);

   const handleSearch = (event) => {
      setSearch(event.target.value);
   };

   const loadmore = () => {
      if (selector.hasMore && selector.loading !== 'pending') {
         setPage(page + 1);
      }
   };
   // get contacts - fetch with pagination (50 items per page)
   React.useEffect(() => {
      dispatch(
         fetchContacts({
            page,
            take: 50,
         })
      );
   }, [page, dispatch]);

   // Memoized sorted and filtered contacts
   const sorted_contacts = React.useMemo(() => {
      let result = [...selector.contacts].sort((a, b) => {
         const nameA = a.fullName.toLowerCase();
         const nameB = b.fullName.toLowerCase();
         if (nameA < nameB) return -1;
         if (nameA > nameB) return 1;
         return 0;
      });

      if (search.trim()) {
         result = result.filter(
            (item) =>
               item.fullName.toLowerCase().includes(search.toLowerCase()) ||
               item.number.includes(search)
         );
      }

      setSorted(result);
      return result;
   }, [selector.contacts, search]);

   const label = { inputProps: { 'aria-label': 'Checkbox demo' } };

   const handleCheckboxClick = React.useCallback(
      (e) => {
         const { value, checked } = e.target;

         if (checked) {
            setCheckedList([...checkedList, value * 1]);
         } else {
            setCheckedList(checkedList.filter((item) => item != value));
         }
      },
      [checkedList]
   );

   // Batch load all contacts when select all is clicked
   const loadAllContactsForSelectAll = React.useCallback(async () => {
      setSelectAllLoading(true);
      try {
         const batchSize = 5000;
         const totalPages = Math.ceil(selector.totalContacts / batchSize);
         const promises = [];

         // Fetch all pages in parallel batches
         for (let i = 1; i <= totalPages; i++) {
            promises.push(
               dispatch(fetchContacts({ page: i, take: batchSize }))
            );
         }

         // Wait for all batches to load
         await Promise.all(promises);

         // Once all loaded, select all
         const allIds = selector.contacts.map((c) => c.id);
         setCheckedList(allIds);
         setItemsChecked(true);
      } catch (error) {
         console.error('Error loading all contacts for select all:', error);
      } finally {
         setSelectAllLoading(false);
      }
   }, [selector.totalContacts, selector.contacts, dispatch]);

   // Handle Select All with batch loading
   const selectItem = React.useCallback(
      (e) => {
         const { checked } = e.target;

         if (checked) {
            // Check if we've loaded all contacts
            if (
               selector.totalContacts &&
               selector.contacts.length < selector.totalContacts
            ) {
               // Need to load all contacts before selecting all
               loadAllContactsForSelectAll();
            } else {
               // All contacts already loaded, select them
               const collection = sorted_contacts.map((item) => item.id);
               setCheckedList(collection);
               setItemsChecked(true);
            }
         } else {
            setCheckedList([]);
            setItemsChecked(false);
         }
      },
      [
         sorted_contacts,
         selector.totalContacts,
         selector.contacts.length,
         loadAllContactsForSelectAll,
      ]
   );

   // Memoized contact list mapping
   const contactList = React.useMemo(() => {
      const checkedSet = new Set(checkedList);
      return selector.contacts.filter((item) => checkedSet.has(item.id));
   }, [checkedList, selector.contacts]);

   return (
      <Container>
         <Card
            sx={{
               width: '90%',
               maxWidth: '615px',
               margin: '2rem auto',
               position: 'relative',
               boxShadow:
                  '0px 1px 2px rgba(0, 0, 0, 0.3), 0px 2px 6px 2px rgba(0, 0, 0, 0.15)',
               padding: '20px ',
               background: '#EFEEF4',
               minHeight: '80vh',
               borderTopLeftRadius: '25px',
               borderTopRightRadius: '25px',
               borderBottomLeftRadius: 0,
               borderBottomRightRadius: 0,
            }}
         >
            <Box
               sx={{
                  margin: '0 auto',
                  position: 'relative',
                  border: '1px solid transparent',
               }}
            >
               <img
                  src={backImg}
                  alt="back"
                  style={{
                     position: 'absolute',
                     width: '40px',
                     cursor: 'pointer',
                     top: 0,
                  }}
                  onClick={() => window.history.back()}
               />
               <Typography
                  sx={{
                     width: 'max-content',
                     margin: '1rem auto',
                     fontWeight: 600,
                     fontSize: 'clamp(1.1rem, 2vw, 1.25rem)',
                     color: '#0F0E0E',
                  }}
               >
                  Select Contacts
               </Typography>
               <Typography
                  sx={{
                     display: 'flex',
                     alignItems: 'center',
                     fontSize: 'clamp(1rem, 1.5vw, 1.1rem)',
                     fontWeight: 600,
                     position: 'relative',
                  }}
               >
                  <Checkbox
                     disabled={
                        selector.loading == 'pending' || selectAllLoading
                     }
                     checked={itemsChecked}
                     onClick={selectItem}
                     {...label}
                  />{' '}
                  Select All
                  {selectAllLoading && (
                     <CircularProgress
                        size="1.5rem"
                        sx={{ marginLeft: '0.5rem' }}
                     />
                  )}
               </Typography>
               {selectAllLoading && (
                  <Typography
                     sx={{
                        fontSize: '0.85rem',
                        color: '#666',
                        marginLeft: '1.5rem',
                        marginTop: '0.25rem',
                     }}
                  >
                     Loading all {selector.totalContacts?.toLocaleString() || 0}{' '}
                     contacts...
                  </Typography>
               )}
               <FormControl
                  sx={{ width: '100%', position: 'relative', margin: '1rem 0' }}
               >
                  <FaSearch
                     color="#374DA0"
                     style={{
                        zIndex: 2,
                        position: 'absolute',
                        top: '50%',
                        left: '20px',
                        transform: 'translateY(-50%)',
                        fontSize: '0.9rem',
                     }}
                  />
                  {search !== '' && (
                     <FaTimes
                        color="#374DA0"
                        style={{
                           zIndex: 2,
                           position: 'absolute',
                           top: '50%',
                           right: '20px',
                           transform: 'translateY(-50%)',
                           fontSize: '0.9rem',
                           cursor: 'pointer',
                        }}
                        onClick={() => setSearch('')}
                     />
                  )}
                  <TextInput
                     fullWidth
                     placeholder="search contacts"
                     InputProps={{
                        style: {
                           color: '#374DA0',
                           borderRadius: '25px',
                           paddingLeft: '35px',
                           fontFamily: "'Raleway', 'Montserrat', sans-serif",
                           fontSize: '1rem',
                        },
                     }}
                     value={search}
                     onChange={(event) => handleSearch(event)}
                  />
               </FormControl>
               <Box
                  sx={{
                     marginTop: '2rem',
                     height: '42vh',
                     overflowY: 'auto',
                  }}
                  id="scrollableDiv"
               >
                  <InfiniteScroll
                     dataLength={selector.contacts.length}
                     next={loadmore}
                     hasMore={selector.hasMore && !selectAllLoading}
                     loader={
                        <h4 style={{ textAlign: 'center' }}>Loading...</h4>
                     }
                     scrollableTarget="scrollableDiv"
                     endMessage={
                        sorted_contacts.length > 0 && (
                           <p style={{ textAlign: 'center' }}>
                              Yay! You have seen it all
                           </p>
                        )
                     }
                  >
                     {selector.contacts.length > 0 ? (
                        sorted_contacts.map((item, index) => {
                           return (
                              <Card
                                 sx={{
                                    display: 'flex',
                                    paddingY: '10px',
                                    paddingLeft: '15px',
                                    marginBottom: '10px',
                                    cursor: 'pointer',
                                    '&:hover': {
                                       boxShadow:
                                          '0px 0px 1px 0px rgba(0,0,0,0.75)',
                                    },
                                 }}
                                 key={index}
                              >
                                 <Checkbox
                                    value={item.id}
                                    checked={checkedList.includes(item.id)}
                                    onClick={handleCheckboxClick}
                                    {...label}
                                 />
                                 <Box sx={{ paddingLeft: '10px' }}>
                                    <Typography
                                       sx={{
                                          fontSize:
                                             'clamp(0.9rem, 1.5vw, 1rem)',
                                          fontWeight: 600,
                                       }}
                                    >
                                       {item.fullName}
                                    </Typography>
                                    <Typography
                                       sx={{
                                          fontSize:
                                             'clamp(0.9rem, 1.5vw, 1rem)',
                                          fontWeight: 600,
                                       }}
                                    >
                                       {item.number}
                                    </Typography>
                                 </Box>
                              </Card>
                           );
                        })
                     ) : (
                        <Box sx={{ textAlign: 'center', marginTop: '2rem' }}>
                           <Box>
                              <img src={noTransIcon} alt="icon" />
                           </Box>
                           <Typography
                              sx={{
                                 fontWeight: 600,
                                 fontSize: 'clamp(1rem, 1.5vw, 1.1rem)',
                                 color: '#374da0',
                                 marginTop: '1rem',
                                 marginBottom: '1rem',
                              }}
                           >{`Hey ${
                              userLSAuth()?.fullName.split(' ')[0]
                           }  your contacts will appear here`}</Typography>
                        </Box>
                     )}
                     {search !== '' && sorted_contacts.length === 0 && (
                        <Typography
                           sx={{
                              textAlign: 'center',
                              fontSize: 'clamp(1rem, 2vw, 1.2rem)',
                              fontWeight: '600',
                              marginTop: '2rem',
                           }}
                        >
                           Oops No Search result!
                        </Typography>
                     )}
                  </InfiniteScroll>
               </Box>
               <Button
                  variant="contained"
                  sx={{
                     width: '100%',
                     padding: '7px',
                     fontSize: 'clamp(0.9rem, 1.5vw, 1rem)',
                     margin: '2rem 0 1rem 0',
                  }}
                  onClick={() => {
                     setLoading(true);
                     dispatch(addSelectedContacts(contactList));
                     window.history.back();
                  }}
                  disabled={
                     selector.loading == 'pending' ||
                     loading ||
                     selectAllLoading
                  }
                  startIcon={
                     (selector.loading == 'pending' || selectAllLoading) && (
                        <CircularProgress size="1rem" color="primary" />
                     )
                  }
               >
                  {selector.loading == 'pending' || loading || selectAllLoading
                     ? 'Loading...'
                     : `Add contact${checkedList.length > 1 ? 's' : ''} (${
                          checkedList.length
                       })`}
               </Button>
            </Box>
         </Card>
      </Container>
   );
};

export default ContactSelect;
